import ClearIcon from '@mui/icons-material/Clear'
import SearchIcon from '@mui/icons-material/Search'
import { Button, Drawer, Grid, useMediaQuery, useTheme } from '@mui/material'
import { useCallback, useState } from 'react'

import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'
import CategorySelectSearch from '../../../../../components/SelectSearch/CategorySelectSearch'
import ProductSelectSearch from '../../../../../components/SelectSearch/ProductSelectSearch'
import SelectSearchMultiple, {
  IOptionSelect,
} from '../../../../../components/SelectSearchMultiple'
import { ONLY_DATE_FORMAT } from '../../../../../constants/global'
import useAxios from '../../../../../hooks/useAxios'
import { ICategory } from '../../../../../types/category'
import { IDistributionChannel } from '../../../../../types/distribution-channel'
import { PaginationResponseInterface } from '../../../../../types/pagination'
import { IProduct } from '../../../../../types/product'
import { IFilterMapped } from './DailyReportFilter'

interface DailyReportFilterDrawerProps {
  isOpen: boolean
  onClose: () => void
  filter: IFilterMapped
  onFilterChange: (filter: IFilterMapped) => void
  onSearch: (filter: IFilterMapped) => void
}

const menuItemsTypeDateExpire = [
  {
    label: 'ทั้งหมด',
    value: '',
  },
  {
    label: 'ใช้งานแล้ว',
    value: '2',
  },
  {
    label: 'หมดอายุแล้ว',
    value: '3',
  },
]

const menuItemsTypePhoneStatus = [
  {
    label: 'ทั้งหมด',
    value: undefined,
  },
  {
    label: 'รอทำรายการ',
    value: '1',
  },
  {
    label: 'ไม่รับสาย',
    value: '2',
  },
]

const menuItemsTypePipelineStatus = [
  {
    label: 'ทั้งหมด',
    value: '',
  },
  {
    label: 'ใช้งาน',
    value: '1',
  },
  {
    label: 'หมดอายุ',
    value: '2',
  },
]

const DailyReportFilterDrawer = ({
  isOpen,
  onClose,
  onFilterChange,
  filter,
  onSearch,
}: DailyReportFilterDrawerProps) => {
  const { startDate, endDate, categories, products, distribution } = filter

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [searchProduct, setSearchProduct] = useState<string>('')
  const [searchCategory, setSearchCategory] = useState<string>('')
  const [searchDistributionChannel, setSearchDistributionChannel] =
    useState<string>('')
  const [resetAutoComplete, setResetAutoComplete] = useState<string>('')

  const handleFilterChange = useCallback(
    (newFilter: IFilterMapped) => {
      onFilterChange({ ...filter, ...newFilter })
    },
    [filter, onFilterChange],
  )

  const handleReset = useCallback(() => {
    onFilterChange({
      startDate: dayjs().format(),
      categories: undefined,
      products: undefined,
      productGroups: undefined,
      distribution: undefined,
    })

    onSearch({
      startDate: dayjs().format(),
      categories: undefined,
      products: undefined,
      productGroups: undefined,
      distribution: undefined,
    })
    setResetAutoComplete(new Date().getTime().toString())
  }, [startDate, categories, products, distribution, onFilterChange, onSearch])

  const productsList = useAxios<PaginationResponseInterface<IProduct>>({
    method: 'GET',
    url: '/product',
    params: {
      page: 1,
      perPage: 5,
      search: searchProduct,
      startDate: '',
      endDate: '',
    },
  })

  const handleSearchProduct = async (value: string) => {
    setSearchProduct(value)
    await productsList.mutate()
  }

  const categoriesList = useAxios<PaginationResponseInterface<ICategory>>({
    method: 'GET',
    url: '/category',
    params: {
      page: 1,
      perPage: 5,
      search: searchCategory,
      startDate: '',
      endDate: '',
    },
  })

  const handleSearchCategory = async (value: string) => {
    setSearchCategory(value)
    await categoriesList.mutate()
  }

  const distributionChannels = useAxios<
    PaginationResponseInterface<IDistributionChannel>
  >({
    method: 'GET',
    url: '/distribution-channels',
    params: {
      page: 1,
      perPage: 5,
      search: searchDistributionChannel,
      startDate: '',
      endDate: '',
    },
  })

  const handleSearchDistributionChannel = async (value: string) => {
    setSearchDistributionChannel(value)
    try {
      await distributionChannels.mutate()
    } catch (error) {}
  }

  return (
    <Drawer
      anchor={'right'}
      open={isOpen}
      onClose={onClose}
      keepMounted
      PaperProps={{
        style: {
          width: isMobile ? '80%' : '40%',
          maxWidth: isMobile ? '80%' : '40%',
        },
      }}
    >
      <Grid container spacing={2} sx={{ padding: 2 }}>
        <Grid item xs={12} lg={6}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              format={ONLY_DATE_FORMAT}
              // disableFuture
              label="เลือกวันที่เริ่มต้น"
              value={startDate ? dayjs(startDate) : null}
              onChange={(e) =>
                onFilterChange({ ...filter, startDate: e!.format() })
              }
              sx={{ width: '100%' }}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={12} lg={6}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              format={ONLY_DATE_FORMAT}
              // disableFuture
              label="เลือกวันที่สิ้นสุด"
              value={endDate ? dayjs(endDate) : null}
              onChange={(e) =>
                onFilterChange({ ...filter, endDate: e!.format() })
              }
              sx={{ width: '100%' }}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={12} lg={6}>
          <SelectSearchMultiple<any>
            resetAutoComplete={resetAutoComplete}
            placeholder={'เลือกช่องทางการขาย'}
            label={'เลือกช่องทางการขาย'}
            onSelect={(e) => {
              const selectedChannelIds = e?.map(
                (channel) => channel?.value?.objectId,
              )
              onFilterChange({ ...filter, distribution: selectedChannelIds })
            }}
            search={searchDistributionChannel}
            onSearch={handleSearchDistributionChannel}
            options={
              distributionChannels.data?.records
                .filter(
                  (distributionChannel: IDistributionChannel) =>
                    !distribution?.includes(distributionChannel.objectId),
                )
                .map((distributionChannel: IDistributionChannel) => ({
                  label: distributionChannel.name,
                  value: distributionChannel,
                })) || ([] as IOptionSelect<IDistributionChannel>[])
            }
          />
        </Grid>
        <Grid item xs={12} lg={6}>
          {/* <SelectSearchMultiple<IProduct>
            resetAutoComplete={resetAutoComplete}
            placeholder={'เลือกสินค้า'}
            label={'เลือกสินค้า'}
            onSelect={(e) => {
              const selectedProductIds = e?.map(
                (product) => product?.value?.objectId,
              )
              onFilterChange({ ...filter, products: selectedProductIds })
            }}
            search={searchProduct}
            onSearch={handleSearchProduct}
            options={
              productsList?.data?.records
                .filter(
                  (product: IProduct) =>
                    product.isAvailable !== false &&
                    !products?.includes(product.objectId),
                )
                .map(
                  (product: IProduct) =>
                    product.isAvailable && {
                      label: `${product.name} (${product.code})`,
                      image: product.image,
                      value: product,
                    },
                )
                .reduce((acc: IOptionSelect<IProduct>[], product) => {
                  if (product) {
                    acc.push(product)
                  }
                  return acc
                }, []) || ([] as IOptionSelect<IProduct>[])
            }
          /> */}
          <ProductSelectSearch
            resetAutoComplete={resetAutoComplete}
            products={products}
            handleFilterChange={handleFilterChange}
            onSelect={(e) => {
              const selectedProductIds = e?.map(
                (product: any) => product?.value?.objectId,
              )
              onFilterChange({ ...filter, products: selectedProductIds })
            }}
          />
        </Grid>
        <Grid item xs={12} lg={6}>
          {/* <SelectSearchMultiple<ICategory>
            resetAutoComplete={resetAutoComplete}
            placeholder={'เลือกหมวดหมู่สินค้า'}
            label={'เลือกหมวดหมู่สินค้า'}
            onSelect={(e) => {
              const selectedCategoryIds = e?.map(
                (category) => category?.value?.objectId,
              )
              onFilterChange({ ...filter, categories: selectedCategoryIds })
            }}
            search={searchCategory}
            onSearch={handleSearchCategory}
            options={
              categoriesList.data?.records
                .filter(
                  (category: ICategory) =>
                    !categories?.includes(category.objectId),
                )
                .map(
                  (category: ICategory) =>
                    category.isAvailable && {
                      label: category.name,
                      value: category,
                    },
                )
                .reduce((acc: IOptionSelect<ICategory>[], category) => {
                  if (category) {
                    acc.push(category)
                  }
                  return acc
                }, []) || ([] as IOptionSelect<ICategory>[])
            }
          /> */}
          <CategorySelectSearch
            resetAutoComplete={resetAutoComplete}
            categories={categories}
            onSelect={(e) => {
              const selectedCategoryIds = e?.map(
                (category: any) => category?.value?.objectId,
              )
              onFilterChange({ ...filter, categories: selectedCategoryIds })
            }}
          />
        </Grid>
        <Grid item xs={9} lg={9}>
          <Button
            color="primary"
            size="large"
            fullWidth
            variant="contained"
            startIcon={<SearchIcon />}
            onClick={() => onSearch(filter)}
            sx={{ height: '100%' }}
          >
            ค้นหา
          </Button>
        </Grid>
        <Grid item xs={3} lg={3}>
          <Button
            color="inherit"
            size="large"
            fullWidth
            variant="contained"
            startIcon={!isMobile && <ClearIcon />}
            onClick={() => handleReset()}
            sx={{ height: '100%' }}
          >
            {isMobile ? <ClearIcon /> : 'รีเซ็ต'}
          </Button>
        </Grid>
      </Grid>
    </Drawer>
  )
}

export default DailyReportFilterDrawer
